import React, { useState, useRef, useEffect } from 'react';

interface Props {
    label?: string;
    onChange?: (value: string) => void;
    name?: string;
    options?: string[];
}

export default function DropdownV2({ label, onChange, options = [
    'School Owner',
    'Principal',
    'Vice Principal',
    'Teacher',
    'Other'
] }: Props) {

    const [value, setValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const showCreateOption = searchTerm.trim() !== '' &&
        !filteredOptions.some(option => option.toLowerCase() === searchTerm.toLowerCase());

    const handleSelect = (option: string) => {
        setValue(option);
        setSearchTerm('');
        setIsOpen(false);
        if (onChange) {
            onChange(option);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setSearchTerm(newValue);
        if (!isOpen) {
            setIsOpen(true);
        }
    };

    const handleInputClick = () => {
        setIsOpen(true);
        setSearchTerm('');
    };

    const handleCreateNew = () => {
        handleSelect(searchTerm.trim());
    };

    return (
        <div className='flex flex-col gap-1' ref={dropdownRef}>
            {!!label && (
                <label className="text-gray-900 text-sm font-medium">
                    {label}
                </label>
            )}
            <div className="relative">
                <div
                    className='w-full flex items-center justify-between gap-2'
                    style={{
                        border: "1px solid #E2E8F1",
                        borderRadius: "14px",
                        backgroundColor: "#FFF",
                        padding: "10px",
                    }}
                >
                    <input
                        ref={inputRef}
                        type="text"
                        value={isOpen ? searchTerm : value}
                        onChange={handleInputChange}
                        onClick={handleInputClick}
                        placeholder="Select"
                        className="flex-1 text-sm outline-none bg-transparent min-w-0"
                        style={{
                            color: (isOpen ? searchTerm : value) ? '#1a1a1a' : '#9ca3af'
                        }}
                    />
                    <button
                        type="button"
                        onClick={() => {
                            setIsOpen(!isOpen);
                            if (!isOpen) {
                                setTimeout(() => inputRef.current?.focus(), 0);
                            }
                        }}
                        className="flex-shrink-0"
                    >
                        <svg
                            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                </div>

                {isOpen && (filteredOptions.length > 0 || showCreateOption) && (
                    <div
                        className="absolute z-10 w-full mt-1 bg-[white] shadow-lg"
                        style={{
                            border: "1px solid #E2E8F1",
                            borderRadius: "14px",
                            overflow: "hidden"
                        }}
                    >
                        {filteredOptions.map((option, index) => (
                            <button
                                key={option}
                                type="button"
                                onClick={() => handleSelect(option)}
                                className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${(index !== filteredOptions.length - 1 || showCreateOption) ? 'border-b border-gray-100' : ''
                                    }`}
                                style={{
                                    color: value === option ? '#000' : '#1a1a1a'
                                }}
                            >
                                {option}
                            </button>
                        ))}
                        {showCreateOption && (
                            <button
                                type="button"
                                onClick={handleCreateNew}
                                className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors"
                                style={{
                                    color: '#2563eb'
                                }}
                            >
                                Create "{searchTerm.trim()}"
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}